import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getModels, getBotLogsList, getBotLogDetail } from '../api'
import { Pagination, Select, PageSection, FilterGroup, FiltersRow } from '../components/ui'
import GraphLogDetail from '../components/GraphLogDetail'
import { useI18n } from '../i18n'
import { formatDurationFromTimestamps, formatInvokedAt } from '../utils/logFormatters'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'
import '../styles/pages/chats.css'

function LogListItem({ log, selected, onSelect, t, lang }) {
  const initiatedLabel = log.initiated_by === 'fan' ? t('logs.initiatedBy.fan') : t('logs.initiatedBy.system')
  const time = formatInvokedAt(log.created_at)
  const preview = (log.input_preview || '').slice(0, 80)
  const duration = formatDurationFromTimestamps(log.created_at, log.completed_at, lang)
  return (
    <button
      type="button"
      className={`chats-list-item ${selected ? 'chats-list-item--active' : ''}`}
      onClick={() => onSelect(log)}
    >
      <div className="chats-list-item-avatar logs-status-badge" data-status={log.status}>
        {log.status?.[0] || '?'}
      </div>
      <div className="chats-list-item-body">
        <div className="chats-list-item-name">{log.fan_username || log.fan_name || `ID ${log.fan_id}`}</div>
        <div className="chats-list-item-model">{log.model_email || t('common.notAvailable')}</div>
        <div className="chats-list-item-preview">{preview || initiatedLabel}</div>
        <div className="chats-list-item-time">
          {time ? `${time} · ` : ''}{log.state_before || '—'} → {log.state_after || '—'}
        </div>
      </div>
      {duration && <span className="chats-list-item-duration">{duration}</span>}
    </button>
  )
}

export default function LogsPage() {
  const { t, lang } = useI18n()
  const { pathname } = useLocation()
  const routePrefix = pathname.startsWith('/v2') ? '/v2' : '/old'
  const [models, setModels] = useState([])
  const [modelId, setModelId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [logs, setLogs] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingModels, setLoadingModels] = useState(true)
  const [error, setError] = useState(null)
  const [selectedLog, setSelectedLog] = useState(null)
  const [logDetail, setLogDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [page, setPage] = useState(0)
  const limit = 50

  useEffect(() => {
    getModels()
      .then((r) => {
        const list = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : [])
        setModels(list)
      })
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false))
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    const offset = page * limit
    getBotLogsList({
      modelId: modelId || undefined,
      status: statusFilter || undefined,
      limit,
      offset,
    })
      .then((r) => {
        setLogs(r?.data || [])
        setTotal(r?.total ?? 0)
        setSelectedLog(null)
        setLogDetail(null)
        setDetailError(null)
      })
      .catch((err) => {
        setLogs([])
        setTotal(0)
        setError(err?.message || t('logs.error'))
      })
      .finally(() => setLoading(false))
  }, [modelId, statusFilter, page])

  useEffect(() => {
    if (!selectedLog) {
      setLogDetail(null)
      setDetailError(null)
      return
    }
    setLoadingDetail(true)
    setDetailError(null)
    getBotLogDetail(selectedLog.id)
      .then((r) => setLogDetail(r))
      .catch((err) => setDetailError(err?.message || t('chats.logs.error')))
      .finally(() => setLoadingDetail(false))
  }, [selectedLog])

  const showDetailPanel = Boolean(selectedLog)

  const statusOptions = [
    { value: '', label: t('logs.status.all') },
    { value: 'COMPLETED', label: t('logs.status.COMPLETED') },
    { value: 'FAILED', label: t('logs.status.FAILED') },
    { value: 'ROLLBACK', label: t('logs.status.ROLLBACK') },
    { value: 'RUNNING', label: t('logs.status.RUNNING') },
    { value: 'WAITING_LLM', label: t('logs.status.WAITING_LLM') },
    { value: 'PENDING', label: t('logs.status.PENDING') },
  ]

  const modelOptions = models.length
    ? [{ value: '', label: t('chats.filter.allModels') }, ...models.map((m) => ({ value: String(m.id), label: m.of_email || m.name || t('common.notAvailable') }))]
    : [{ value: '', label: t('chats.loading') }]

  return (
    <PageSection
      className="chats-page logs-page"
      title={t('logs.title')}
      subtitle={t('logs.subtitle')}
    >
      <>
        <FiltersRow>
          <FilterGroup label={t('logs.modelFilter')}>
            <Select
              className="filter-select"
              value={modelId != null ? String(modelId) : ''}
              placeholder={t('logs.modelFilter')}
              options={modelOptions}
              onChange={(v) => {
                setModelId(v ? Number(v) : null)
                setPage(0)
              }}
              dropdownPosition="down"
            />
            {modelId && (
              <Link to={`${routePrefix}/models/${modelId}`} className="filter-link">
                {t('chats.toModel')}
              </Link>
            )}
          </FilterGroup>
          <FilterGroup label={t('logs.statusFilter')}>
            <Select
              className="filter-select"
              value={statusFilter}
              placeholder={t('logs.statusFilter')}
              options={statusOptions}
              onChange={(v) => {
                setStatusFilter(v || '')
                setPage(0)
              }}
              dropdownPosition="down"
            />
          </FilterGroup>
        </FiltersRow>
        {loadingModels ? (
        <p className="chats-loading">{t('chats.loadingModels')}</p>
      ) : (
        <div className="chats-layout">
          <aside className="chats-sidebar">
            {loading && logs.length === 0 ? (
              <p className="chats-sidebar-loading">{t('logs.loading')}</p>
            ) : error ? (
              <p className="chats-sidebar-error">{error}</p>
            ) : logs.length === 0 ? (
              <p className="chats-sidebar-empty">{t('logs.empty')}</p>
            ) : (
              <ul className="chats-list">
                {logs.map((log) => (
                  <li key={log.id}>
                    <LogListItem
                      log={log}
                      selected={selectedLog?.id === log.id}
                      onSelect={setSelectedLog}
                      t={t}
                      lang={lang}
                    />
                  </li>
                ))}
              </ul>
            )}
            {total > 0 && (
              <Pagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
                loading={loading}
                t={t}
              />
            )}
          </aside>
          <main className={`chats-main logs-main ${showDetailPanel ? 'chats-main--with-log' : ''}`}>
            {!selectedLog ? (
              <div className="chats-main-placeholder">
                {t('logs.selectLeft')}
              </div>
            ) : loadingDetail ? (
              <p className="chats-main-loading">{t('chats.logs.loading')}</p>
            ) : detailError ? (
              <p className="chats-sidebar-error">{detailError}</p>
            ) : !logDetail ? (
              <p className="chats-log-muted">{t('chats.logs.empty')}</p>
            ) : (
              <div className="chats-log-panel logs-detail-panel">
                <div className="chats-log-header">
                  <div className="chats-log-title-wrap">
                    <h3 className="chats-log-title">{t('logs.detailTitle')}</h3>
                    <span className="chats-log-subtitle">{t('logs.graphId')}: {selectedLog.id.slice(0, 8)}…</span>
                  </div>
                  <button type="button" className="chats-log-close" onClick={() => setSelectedLog(null)}>
                    {t('chats.logs.close')}
                  </button>
                </div>
                <div className="chats-log-content">
                  <GraphLogDetail logData={logDetail} />
                </div>
              </div>
            )}
          </main>
        </div>
      )}
      </>
    </PageSection>
  )
}
